import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { initiateGoogleLogin } from '../../services/api';
import { AccessPendingScreen } from './AccessPendingScreen';
import { AccessDeniedScreen } from './AccessDeniedScreen';

export const LandingPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [acknowledged, setAcknowledged] = useState(false);

  const access = searchParams.get('access');
  const email = searchParams.get('email');

  // Show pending screen if access=pending
  if (access === 'pending') {
    return <AccessPendingScreen email={email || ''} />;
  }

  // Show denied screen if access=denied
  if (access === 'denied') {
    return <AccessDeniedScreen email={email || ''} />;
  }

  const handleSignIn = () => {
    if (acknowledged) {
      initiateGoogleLogin();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-xl p-8">
        {/* Title Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            GPU Cloud BOM Calculator
          </h1>
          <p className="text-lg text-gray-600">
            Plan your GPU infrastructure with confidence
          </p>
        </div>

        {/* Risk Disclaimer Section */}
        <div className="mb-8 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0 text-3xl mr-3">
              ⚠️
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                Important Disclaimer
              </h2>
              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  This calculator is provided for planning purposes only. The calculations,
                  recommendations, and configurations generated by this tool:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Are estimates and may not reflect actual requirements</li>
                  <li>Should be verified by qualified engineers before implementation</li>
                  <li>May contain errors or inaccuracies</li>
                  <li>Do not constitute professional engineering advice</li>
                </ul>
                <p className="font-semibold mt-3">
                  Use of this tool is entirely at your own risk. Always consult with
                  qualified professionals before making infrastructure decisions.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Acknowledgement Checkbox */}
        <div className="mb-6">
          <label className="flex items-start cursor-pointer group">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="mt-1 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            />
            <span className="ml-3 text-sm text-gray-700 group-hover:text-gray-900">
              I acknowledge that I have read and understood the disclaimer above,
              and I accept the risks associated with using this calculator.
            </span>
          </label>
        </div>

        {/* Sign-in Button */}
        <div className="text-center">
          <button
            onClick={handleSignIn}
            disabled={!acknowledged}
            className={`
              inline-flex items-center justify-center gap-3 px-6 py-3 rounded-lg
              font-semibold text-base transition-all duration-200
              ${
                acknowledged
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg cursor-pointer'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            <svg width="20" height="20" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path
                fill="currentColor"
                d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"
              />
              <path
                fill="currentColor"
                d="M9.003 18c2.43 0 4.467-.806 5.956-2.18L12.05 13.56c-.806.54-1.836.86-3.047.86-2.344 0-4.328-1.584-5.036-3.711H.96v2.332C2.44 15.983 5.485 18 9.003 18z"
              />
              <path
                fill="currentColor"
                d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"
              />
              <path
                fill="currentColor"
                d="M9.003 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.464.891 11.428 0 9.003 0 5.485 0 2.44 2.017.96 4.958L3.967 7.29c.708-2.127 2.692-3.71 5.036-3.71z"
              />
            </svg>
            Sign in with Google
          </button>

          {!acknowledged && (
            <p className="mt-3 text-sm text-gray-500">
              Please acknowledge the disclaimer to continue
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 text-center">
          <p className="text-xs text-gray-500">
            By signing in, you agree to our terms of service and privacy policy.
            Access is subject to approval.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
